import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { TrendingDown, Home, Headphones } from "lucide-react";

// Alluz Logo Component
const AlluzLogo = ({ className = "", size = 40 }) => (
  <img 
    src="/logo-alluz.png" 
    alt="Alluz" 
    className={className}
    style={{ width: size, height: size, objectFit: 'contain' }}
  />
);

export default function BentoFeatures() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const features = [
    {
      icon: TrendingDown,
      title: "Redução de Conta",
      description: "Reduza sua conta de energia em até 95% desde o primeiro mês. Economize milhares de reais por ano.",
      highlight: "Até 95%",
      highlightLabel: "de economia"
    },
    {
      icon: Home,
      title: "Valorização do Imóvel",
      description: "Imóveis com energia solar valorizam de 3% a 6% no mercado. Um investimento que cresce com você.",
      highlight: "+6%",
      highlightLabel: "valorização"
    },
    {
      icon: Headphones,
      title: "Acompanhamento Posterior",
      description: "Suporte técnico e monitoramento contínuo após a instalação. Você nunca fica sozinho.",
      highlight: "24/7",
      highlightLabel: "suporte"
    },
  ];

  return (
    <section
      id="vantagens"
      ref={ref}
      className="py-20 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-[#FEF3C7] via-[#FFFBEB] to-[#FFF7ED] relative overflow-hidden"
      data-testid="bento-section"
    >
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        <div className="absolute top-20 right-10 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-[#FFB414]/15 to-transparent blur-3xl" />
        <div className="absolute bottom-20 left-10 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-[#FFC940]/15 to-transparent blur-3xl" />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-14 sm:mb-16"
        >
          <motion.div 
            className="inline-flex items-center gap-3 mb-5"
            whileHover={{ scale: 1.05 }}
          >
            <AlluzLogo size={50} />
          </motion.div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-['Outfit'] text-[#92400E] mb-4">
            Por que escolher a{" "}
            <span className="gradient-text">Alluz</span>?
          </h2>
          <p className="text-base sm:text-lg text-[#92400E]/60 max-w-2xl mx-auto px-4">
            Somos seus parceiros na jornada para um futuro mais sustentável e econômico.
          </p>
        </motion.div>

        {/* Cards Grid - All Orange */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={itemVariants}
              className="group cursor-pointer"
              data-testid={`card-${index}`}
            >
              <div className="relative h-full p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#FFB414] to-[#FF8C00] text-white overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#FFB414]/30">
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/30 blur-3xl group-hover:scale-150 transition-transform duration-700" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/20 blur-2xl group-hover:scale-150 transition-transform duration-700" />
                </div>
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-xl sm:text-2xl font-bold font-['Outfit'] mb-3">
                    {feature.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-white/90 text-sm sm:text-base leading-relaxed mb-5">
                    {feature.description}
                  </p>
                  
                  {/* Highlight */}
                  <div className="flex items-end gap-2 pt-2 border-t border-white/20">
                    <span className="text-3xl sm:text-4xl font-black font-['Outfit'] text-white/90">
                      {feature.highlight}
                    </span>
                    <span className="text-white/70 text-sm pb-1">
                      {feature.highlightLabel}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
